/**
 * 마스크·선택 연산. **서피스를 인자로 받는 순수 함수**다 (설계문서 §5.4).
 *
 * Canvas2D를 붙들지 않는 이유: cpapp의 꾸미기 캔버스가 Skia를 쓰게 되면
 * 같은 연산을 Skia 서피스 위에서 돌려야 한다. 블렌드 모드가 1:1 대응하므로
 * 여기 코드는 그대로 재사용된다.
 */
import type { Op, Point, Rect, SelectStroke, Surface } from './types.js';

/** AI 마스크에서 "피사체"로 볼 알파 하한. */
export const SUBJECT_THRESHOLD = 128;

/**
 * 클릭한 지점이 속한 **연결 영역**을 스캔라인 flood fill 로 찾는다.
 *
 * 사진에 피사체가 여럿이면 누른 것만 잡힌다 — 전역 마스크를 통째로 쓰면
 * 셋 다 잡히므로 "이 사람만"이 안 된다.
 *
 * 가로 띠(span) 목록으로 돌려준다. 픽셀을 하나씩 칠하면 느리고, 띠로 묶으면
 * `fillRects` 한 번으로 끝난다.
 */
export function floodRegion(
  alpha: Uint8Array,
  w: number,
  h: number,
  sx: number,
  sy: number,
  threshold = SUBJECT_THRESHOLD,
): Rect[] {
  const x0 = Math.round(sx), y0 = Math.round(sy);
  if (x0 < 0 || y0 < 0 || x0 >= w || y0 >= h) return [];
  if (alpha[y0 * w + x0]! < threshold) return [];   // 배경을 눌렀다

  const seen = new Uint8Array(w * h);
  const rects: Rect[] = [];
  const stack: number[] = [x0, y0];

  while (stack.length) {
    const y = stack.pop()!;
    let x = stack.pop()!;
    let row = y * w;
    if (seen[row + x]) continue;

    // 좌우로 훑어 이 줄의 띠를 구한다
    let left = x;
    while (left > 0 && !seen[row + left - 1] && alpha[row + left - 1]! >= threshold) left--;
    let right = x;
    while (right < w - 1 && !seen[row + right + 1] && alpha[row + right + 1]! >= threshold) right++;

    for (let i = left; i <= right; i++) seen[row + i] = 1;
    rects.push({ x: left, y, w: right - left + 1, h: 1 });

    // 위·아래 줄에서 이어지는 곳을 씨앗으로
    for (const ny of [y - 1, y + 1]) {
      if (ny < 0 || ny >= h) continue;
      const nrow = ny * w;
      let i = left;
      while (i <= right) {
        if (!seen[nrow + i] && alpha[nrow + i]! >= threshold) {
          stack.push(i, ny);
          while (i <= right && alpha[nrow + i]! >= threshold) i++;   // 같은 띠는 한 번만
        }
        i++;
      }
    }
  }
  return rects;
}

/**
 * 선택 동작 하나를 대상 서피스에 반영한다.
 *
 * `scratch` 는 `add`/`replace` + `refine` 조합에서만 쓴다 --
 * "올가미 ∩ AI" 를 따로 만든 뒤 얹어야 하기 때문이다.
 *
 * `subtract` 는 AI 를 끼우지 않는다. 지우는 동작에까지 AI 가 개입하면
 * "왜 안 지워지지"가 된다.
 */
export function applyStroke(
  target: Surface,
  stroke: SelectStroke,
  ai: Surface | null,
  scratch: Surface | null = null,
  aiAlpha: Uint8Array | null = null,
): void {
  const { kind, mode, pts } = stroke;

  if (kind === 'point') {
    // 클릭 선택 — AI 마스크에서 누른 지점의 연결 영역만.
    if (!ai || !scratch || !aiAlpha || !pts?.length) return;
    const rects = floodRegion(aiAlpha, target.width, target.height, pts[0]![0], pts[0]![1]);
    if (!rects.length) return;                 // 배경을 눌렀다 -- 아무것도 안 한다
    scratch.clear();
    scratch.fillRects(rects, 'srcOver');
    // 덩어리 안에서 **AI 의 부드러운 알파를 되살린다.** 띠만 쓰면 경계가 각진다.
    scratch.drawImage(ai.asImage(), 'dstIn');
    if (mode === 'subtract') target.drawImage(scratch.asImage(), 'dstOut');
    else if (mode === 'add') target.drawImage(scratch.asImage(), 'srcOver');
    else target.drawImage(scratch.asImage(), 'copy');
    return;
  }

  if (kind === 'auto') {
    // 영역 자동맞춤 — AI 마스크를 통째로.
    if (!ai) return;
    if (mode === 'subtract') target.drawImage(ai.asImage(), 'dstOut');
    else if (mode === 'add') target.drawImage(ai.asImage(), 'srcOver');
    else target.drawImage(ai.asImage(), 'copy');
    return;
  }

  if (!pts || pts.length < 3) return;

  if (mode === 'subtract') {
    target.fillPath(pts, 'dstOut');
    return;
  }

  const refine = stroke.refine && !!ai;

  if (!refine) {
    if (mode === 'replace') target.clear();
    target.fillPath(pts, 'srcOver');
    return;
  }

  if (!scratch) {
    // 스크래치가 없으면 refine 을 포기한다. 조용히 틀리느니 모양만 쓴다.
    if (mode === 'replace') target.clear();
    target.fillPath(pts, 'srcOver');
    return;
  }

  // region = 올가미 ∩ AI
  scratch.clear();
  scratch.fillPath(pts, 'srcOver');
  scratch.drawImage(ai!.asImage(), 'dstIn');

  if (mode === 'replace') target.drawImage(scratch.asImage(), 'copy');
  else target.drawImage(scratch.asImage(), 'srcOver');
}

/** 선택 동작 목록을 빈 상태에서부터 다시 그린다. */
export function buildSelection(
  target: Surface,
  strokes: readonly SelectStroke[],
  ai: Surface | null,
  scratch: Surface | null = null,
  aiAlpha: Uint8Array | null = null,
): void {
  target.clear();
  for (const s of strokes) applyStroke(target, s, ai, scratch, aiAlpha);
}

/** 명령 하나를 마스크에 적용한다. `ai`가 없으면 auto/refine은 조용히 건너뛴다. */
export function applyOp(
  mask: Surface,
  op: Op,
  ai: Surface | null,
  scratch: Surface | null = null,
  aiAlpha: Uint8Array | null = null,
): void {
  switch (op.t) {
    case 'auto':
      // 마스크 := AI 마스크 (프로그램적 경로. 에디터는 select 를 쓴다)
      if (ai) mask.drawImage(ai.asImage(), 'copy');
      break;

    case 'reset':
      mask.fillAll('copy');
      break;

    case 'select':
      // 확정된 선택. 마스크 := 선택 영역.
      // 픽셀이 아니라 **선택을 만든 동작들**을 다시 재생하므로 undo 가 정확하다.
      buildSelection(mask, op.strokes, ai, scratch, aiAlpha);
      break;
  }
}

/**
 * 명령 로그를 처음부터 다시 그린다.
 *
 * undo가 픽셀 스냅샷 대신 이걸 쓴다: 메모리가 좌표라 수백 배 작고 redo가 공짜다.
 * 명령 하나당 GPU 드로우 한 번이라 수백 개도 몇 ms다.
 */
export function rebuild(
  mask: Surface,
  ops: readonly Op[],
  ai: Surface | null,
  base: Surface | null,
  scratch: Surface | null = null,
  aiAlpha: Uint8Array | null = null,
): void {
  if (base) mask.drawImage(base.asImage(), 'copy');
  else mask.fillAll('copy');   // 베이크 전 시작 상태 = 전부 보임
  for (const op of ops) applyOp(mask, op, ai, scratch, aiAlpha);
}
